import { useState } from 'react';
import { motion } from 'framer-motion';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Receipt,
  Plus,
  Search,
  Edit,
  AlertTriangle,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  TrendingUp,
  DollarSign,
} from 'lucide-react';
import { Card, CardBody, CardHeader, CardTitle, Badge, Button, Input, Modal, Select } from '../../components/ui';
import { api } from '../../lib/api';
import { ENDPOINTS } from '../../config/api';

interface Fee {
  id: string;
  tenantId: string;
  tenant?: { name: string };
  operationType: string;
  feeType: 'FIXED' | 'PERCENTAGE' | 'MIXED';
  fixedAmount: number;
  percentageRate: number;
  minAmount?: number;
  maxAmount?: number;
  isActive: boolean;
  createdAt: string;
}

interface FeeValidation {
  isValid: boolean;
  margin: number;
  marginPercentage: number;
  realCost: number;
  configuredFee: number;
  warnings: string[];
}

interface FeeAtRisk {
  fee: Fee;
  margin: number;
  marginPercentage: number;
  realCost: number;
}

interface Tenant {
  id: string;
  name: string;
}

const OPERATION_TYPES = [
  { value: 'PIX_SEND', label: 'Pix - Envio' },
  { value: 'PIX_RECEIVE', label: 'Pix - Recebimento' },
  { value: 'BOLETO', label: 'Boleto' },
  { value: 'CARD', label: 'Cartão de Crédito' },
  { value: 'TRANSFER', label: 'Transferência Interna' },
  { value: 'WITHDRAW', label: 'Saque' },
];

export function AdminFees() {
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingFee, setEditingFee] = useState<Fee | null>(null);
  const [validation, setValidation] = useState<FeeValidation | null>(null);
  const [formData, setFormData] = useState({
    tenantId: '',
    operationType: 'PIX_SEND',
    feeType: 'FIXED' as 'FIXED' | 'PERCENTAGE' | 'MIXED',
    fixedAmount: 0,
    percentageRate: 0,
    minAmount: 0,
    maxAmount: 0,
  });

  // Buscar tenants
  const { data: tenantsData } = useQuery({
    queryKey: ['tenants-list'],
    queryFn: async () => {
      const response = await api.get(ENDPOINTS.tenants.list);
      return response.data as { tenants: Tenant[] };
    },
  });

  // Buscar taxas em risco
  const { data: feesAtRiskData } = useQuery({
    queryKey: ['fees-at-risk'],
    queryFn: async () => {
      const response = await api.get(ENDPOINTS.costs.feesAtRisk);
      return response.data as { feesAtRisk: FeeAtRisk[] } | FeeAtRisk[];
    },
  });

  // Buscar todas as taxas configuradas
  const { data: feesData, isLoading, error, refetch } = useQuery({
    queryKey: ['admin-fees'],
    queryFn: async () => {
      const response = await api.get(ENDPOINTS.costs.feeConfigurations);
      return response.data as { fees: Fee[]; total: number };
    },
  });

  // Validar taxa
  const validateMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      const response = await api.post(ENDPOINTS.costs.validateFee, {
        tenantId: data.tenantId,
        operationType: data.operationType,
        feeType: data.feeType,
        fixedAmount: data.fixedAmount * 100, // converter para centavos
        percentageRate: data.percentageRate,
      });
      return response.data as FeeValidation;
    },
    onSuccess: (data) => {
      setValidation(data);
    },
  });

  // Criar/atualizar taxa
  const saveMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      const payload = {
        ...data,
        fixedAmount: data.fixedAmount * 100, // converter para centavos
      };
      if (editingFee) {
        const response = await api.put(`/fees/${editingFee.id}`, payload);
        return response.data;
      }
      const response = await api.post('/fees', payload);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-fees'] });
      queryClient.invalidateQueries({ queryKey: ['fees-at-risk'] });
      setIsModalOpen(false);
      resetForm();
    },
  });

  const resetForm = () => {
    setFormData({
      tenantId: '',
      operationType: 'PIX_SEND',
      feeType: 'FIXED',
      fixedAmount: 0,
      percentageRate: 0,
      minAmount: 0,
      maxAmount: 0,
    });
    setValidation(null);
    setEditingFee(null);
  };

  const handleEdit = (fee: Fee) => {
    setEditingFee(fee);
    setFormData({
      tenantId: fee.tenantId,
      operationType: fee.operationType,
      feeType: fee.feeType,
      fixedAmount: fee.fixedAmount / 100,
      percentageRate: fee.percentageRate,
      minAmount: fee.minAmount ? fee.minAmount / 100 : 0,
      maxAmount: fee.maxAmount ? fee.maxAmount / 100 : 0,
    });
    setIsModalOpen(true);
  };

  const handleValidate = () => {
    if (formData.tenantId) {
      validateMutation.mutate(formData);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    saveMutation.mutate(formData);
  };

  const fees = feesData?.fees || [];
  const feesAtRisk = Array.isArray(feesAtRiskData) 
    ? feesAtRiskData 
    : (feesAtRiskData as any)?.feesAtRisk || (feesAtRiskData as any)?.data || [];
  const tenants = tenantsData?.tenants || (tenantsData as any)?.data || [];

  const filteredFees = fees.filter(fee =>
    fee.tenant?.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    fee.operationType.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatCurrency = (cents: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(cents / 100);
  };

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-96 gap-4">
        <AlertCircle className="w-16 h-16 text-red-400" />
        <h2 className="text-xl font-semibold text-surface-100">Erro ao carregar taxas</h2>
        <Button variant="primary" onClick={() => refetch()}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Tentar novamente
        </Button>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-surface-100">Gestão de Taxas</h1>
          <p className="text-surface-400">Configure taxas por tenant com validação anti-buraco contábil</p>
        </div>
        <Button variant="primary" onClick={() => { resetForm(); setIsModalOpen(true); }}>
          <Plus className="w-4 h-4 mr-2" />
          Nova Taxa
        </Button>
      </div>

      {/* Alertas de Taxas em Risco */}
      {feesAtRisk.length > 0 && (
        <Card variant="glass">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-warning">
              <AlertTriangle className="w-5 h-5" />
              Taxas em Risco ({feesAtRisk.length})
            </CardTitle>
          </CardHeader>
          <CardBody>
            <div className="space-y-3">
              {feesAtRisk.map((item: FeeAtRisk, index: number) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-3 bg-warning/10 border border-warning/30 rounded-lg"
                >
                  <div>
                    <p className="font-medium text-surface-100">
                      {item.fee.tenant?.name || 'Tenant'} - {item.fee.operationType}
                    </p>
                    <p className="text-sm text-surface-400">
                      Custo real: {formatCurrency(item.realCost)} | Margem: {item.marginPercentage.toFixed(2)}%
                    </p>
                  </div>
                  <Badge variant="warning">
                    Margem baixa
                  </Badge>
                </div>
              ))}
            </div>
          </CardBody>
        </Card>
      )}

      {/* Search */}
      <Card>
        <CardBody>
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <Input
                placeholder="Buscar por tenant ou tipo de operação..."
                leftIcon={<Search className="w-4 h-4" />}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <Button variant="secondary" onClick={() => refetch()} disabled={isLoading}>
              <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </CardBody>
      </Card>

      {/* Fees List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Receipt className="w-5 h-5 text-primary-400" />
            Taxas Configuradas
            <Badge variant="info">{filteredFees.length}</Badge>
          </CardTitle>
        </CardHeader>
        <CardBody className="p-0">
          {isLoading ? (
            <div className="p-6 space-y-4">
              {[1, 2, 3, 4, 5].map(i => (
                <div key={i} className="animate-pulse bg-surface-800 rounded h-20" />
              ))}
            </div>
          ) : filteredFees.length === 0 ? (
            <div className="text-center py-12 text-surface-400">
              <Receipt className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>Nenhuma taxa configurada</p>
            </div>
          ) : (
            <div className="divide-y divide-surface-800">
              {filteredFees.map(fee => (
                <div
                  key={fee.id}
                  className="flex items-center justify-between px-6 py-4 hover:bg-surface-800/30 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-primary-500/20 flex items-center justify-center">
                      <DollarSign className="w-6 h-6 text-primary-400" />
                    </div>
                    <div>
                      <p className="font-medium text-surface-100">{fee.tenant?.name || 'Tenant'}</p>
                      <p className="text-sm text-surface-500">
                        {OPERATION_TYPES.find(t => t.value === fee.operationType)?.label || fee.operationType}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-6">
                    <div className="text-right">
                      <p className="font-medium text-surface-100">
                        {fee.feeType === 'FIXED' && formatCurrency(fee.fixedAmount)}
                        {fee.feeType === 'PERCENTAGE' && `${fee.percentageRate}%`}
                        {fee.feeType === 'MIXED' && `${formatCurrency(fee.fixedAmount)} + ${fee.percentageRate}%`}
                      </p>
                      <p className="text-sm text-surface-500">
                        {fee.feeType === 'FIXED' ? 'Taxa fixa' : fee.feeType === 'PERCENTAGE' ? 'Percentual' : 'Mista'}
                      </p>
                    </div>
                    <Badge variant={fee.isActive ? 'success' : 'error'}>
                      {fee.isActive ? 'Ativa' : 'Inativa'}
                    </Badge>
                    <Button variant="ghost" size="sm" onClick={() => handleEdit(fee)}>
                      <Edit className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardBody>
      </Card>

      {/* Create/Edit Fee Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => { setIsModalOpen(false); resetForm(); }}
        title={editingFee ? 'Editar Taxa' : 'Nova Taxa'}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <Select
            label="Tenant"
            value={formData.tenantId}
            onChange={(e) => setFormData({ ...formData, tenantId: e.target.value })}
            options={[
              { value: '', label: 'Selecione um tenant' },
              ...tenants.map((t: Tenant) => ({ value: t.id, label: t.name })),
            ]}
            required
            disabled={!!editingFee}
          />
          <Select
            label="Tipo de Operação"
            value={formData.operationType}
            onChange={(e) => setFormData({ ...formData, operationType: e.target.value })}
            options={OPERATION_TYPES}
            required
          />
          <Select
            label="Tipo de Taxa"
            value={formData.feeType}
            onChange={(e) => setFormData({ ...formData, feeType: e.target.value as 'FIXED' | 'PERCENTAGE' | 'MIXED' })}
            options={[
              { value: 'FIXED', label: 'Taxa Fixa' },
              { value: 'PERCENTAGE', label: 'Percentual' },
              { value: 'MIXED', label: 'Mista (Fixa + Percentual)' },
            ]}
          />
          {(formData.feeType === 'FIXED' || formData.feeType === 'MIXED') && (
            <Input
              label="Valor Fixo (R$)"
              type="number"
              step="0.01"
              value={formData.fixedAmount}
              onChange={(e) => setFormData({ ...formData, fixedAmount: parseFloat(e.target.value) || 0 })}
            />
          )}
          {(formData.feeType === 'PERCENTAGE' || formData.feeType === 'MIXED') && (
            <Input
              label="Percentual (%)"
              type="number"
              step="0.01"
              value={formData.percentageRate}
              onChange={(e) => setFormData({ ...formData, percentageRate: parseFloat(e.target.value) || 0 })}
            />
          )}
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Valor Mínimo (R$)"
              type="number"
              step="0.01"
              value={formData.minAmount}
              onChange={(e) => setFormData({ ...formData, minAmount: parseFloat(e.target.value) || 0 })}
            />
            <Input
              label="Valor Máximo (R$)"
              type="number"
              step="0.01"
              value={formData.maxAmount}
              onChange={(e) => setFormData({ ...formData, maxAmount: parseFloat(e.target.value) || 0 })}
            />
          </div>

          {/* Validação */}
          <div className="pt-2">
            <Button
              type="button"
              variant="secondary"
              onClick={handleValidate}
              disabled={!formData.tenantId || validateMutation.isPending}
              className="w-full"
            >
              <TrendingUp className="w-4 h-4 mr-2" />
              Validar Taxa (Anti-Buraco Contábil)
            </Button>
          </div>

          {validation && (
            <div className={`p-4 rounded-lg border ${validation.isValid ? 'bg-success/10 border-success/30' : 'bg-error/10 border-error/30'}`}>
              <div className="flex items-center gap-2 mb-2">
                {validation.isValid ? (
                  <CheckCircle className="w-5 h-5 text-success" />
                ) : (
                  <AlertTriangle className="w-5 h-5 text-error" />
                )}
                <span className={`font-medium ${validation.isValid ? 'text-success' : 'text-error'}`}>
                  {validation.isValid ? 'Taxa válida' : 'Taxa inválida'}
                </span>
              </div>
              <div className="text-sm space-y-1 text-surface-300">
                <p>Custo real: {formatCurrency(validation.realCost)}</p>
                <p>Taxa configurada: {formatCurrency(validation.configuredFee)}</p>
                <p>Margem: {formatCurrency(validation.margin)} ({validation.marginPercentage.toFixed(2)}%)</p>
              </div>
              {validation.warnings.length > 0 && (
                <div className="mt-2 text-sm text-warning">
                  {validation.warnings.map((w, i) => (
                    <p key={i}>⚠️ {w}</p>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="secondary" onClick={() => setIsModalOpen(false)}>
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="primary"
              isLoading={saveMutation.isPending}
              disabled={validation ? !validation.isValid : false}
            >
              {editingFee ? 'Salvar' : 'Criar'}
            </Button>
          </div>
        </form>
      </Modal>
    </motion.div>
  );
}
